import { FeatureName } from "@common/ADAPT.Common.Model/embed/feature-name.enum";
import { CulturalActivity } from "@common/ADAPT.Common.Model/organisation/cultural-activity";
import { CulturalActivityType } from "@common/ADAPT.Common.Model/organisation/cultural-activity-type";
import { IAdaptLinkObject } from "@common/route/route.service";
import { KanbanAuthService } from "@org-common/lib/kanban/kanban-auth.service";
import { myKanbanPageRoute, teamKanbanPageRoute } from "@org-common/lib/kanban/kanban-page/kanban-page.route";
import { teamMeetingsPageRoute } from "@org-common/lib/meetings/team-meetings-page/team-meetings-page.route";
import { ObjectivesAuthService } from "@org-common/lib/objectives/objectives-auth.service";
import { organisationObjectivesPageRoute, teamObjectivesPageRoute } from "@org-common/lib/objectives/objectives-page/objectives-page.route";
import { myCatchupsPageRoute } from "@org-common/lib/peer-catchup/my-catchups-page/my-catchups-page.route";
import { PeerCatchupAuthService } from "@org-common/lib/peer-catchup/peer-catchup-auth.service";
import { teamCatchupsPageRoute } from "@org-common/lib/peer-catchup/team-catchups-page/team-catchups-page.route";
import { EmployeeEngagementAuthService } from "@org-common/lib/survey/employee-engagement/employee-engagement-auth.service";
import { manageEmployeeEngagementsPageRoute } from "@org-common/lib/survey/employee-engagement/manage-employee-engagements-page/manage-employee-engagements-page.route";
import { manageRBAssessmentsPageRoute } from "@org-common/lib/survey/resilient-business-assessment/manage-rb-assessments-page/manage-rb-assessments-page.route";
import { ResilientBusinessAssessmentAuthService } from "@org-common/lib/survey/resilient-business-assessment/resilient-business-assessment-auth.service";
import { manageTeamAssessmentsPageRoute } from "@org-common/lib/survey/team-assessment/manage-team-assessments-page/manage-team-assessments-page.route";
import { TeamAssessmentAuthService } from "@org-common/lib/survey/team-assessment/team-assessment-auth.service";
import { CommonTeamsAuthService } from "@org-common/lib/teams/common-teams-auth.service";
import { Observable, ObservableInput } from "rxjs";

export enum NonCulturalActivityType {
    Actions = "Actions",
    Meetings = "Meetings",
    Objectives = "Objectives",
}

export type ActivityType = CulturalActivityType | NonCulturalActivityType;

export interface ICulturalActivityTypeMetadata {
    type: ActivityType;
    title: string;
    shortTitle: string;
    actionText?: string;
    colourAccent: string;
    isCulturalActivityType: boolean;
    feature: FeatureName;
    accessVerifier?: string;
    route$?: (activity?: CulturalActivity, isCulturalLeader?: boolean) => Observable<IAdaptLinkObject>;
    action$?: (activity: CulturalActivity) => ObservableInput<any>;
    routeRequiresTeamId?: boolean;
}

export const CulturalActivityMetadataByType: { [k in ActivityType]: ICulturalActivityTypeMetadata } = {
    [NonCulturalActivityType.Actions]: {
        type: NonCulturalActivityType.Actions,
        title: "Actions",
        shortTitle: "Action",
        colourAccent: "var(--adapt-wise-lime-500)",
        feature: FeatureName.StewardshipWorkKanban,
        accessVerifier: KanbanAuthService.ViewAnyBoard,
        isCulturalActivityType: false,
        route$: (activity) => activity?.teamId
            ? teamKanbanPageRoute.getRouteObject({ teamId: activity?.teamId })
            : myKanbanPageRoute.getRouteObject(),
    },
    [NonCulturalActivityType.Meetings]: {
        type: NonCulturalActivityType.Meetings,
        title: "Meetings",
        shortTitle: "Meeting",
        colourAccent: "var(--adapt-future-lemon-600)",
        feature: FeatureName.StewardshipWorkMeetings,
        accessVerifier: CommonTeamsAuthService.ViewAnyTeamMeeting,
        isCulturalActivityType: false,
        route$: (activity) => teamMeetingsPageRoute.getRouteObject({ teamId: activity?.teamId! }),
        routeRequiresTeamId: true,
    },
    [NonCulturalActivityType.Objectives]: {
        type: NonCulturalActivityType.Objectives,
        title: "Objectives",
        shortTitle: "Objective",
        colourAccent: "var(--adapt-new-day-yellow-500)",
        feature: FeatureName.StewardshipObjectives,
        accessVerifier: ObjectivesAuthService.ReadAnyObjectives,
        isCulturalActivityType: false,
        route$: (activity) => activity?.teamId
            ? teamObjectivesPageRoute.getRouteObject({ teamId: activity?.teamId })
            : organisationObjectivesPageRoute.getRouteObject({}, { showAllTeamObjectives: true }),
    },
    [CulturalActivityType.CareerValuation]: {
        type: CulturalActivityType.CareerValuation,
        title: "Career valuations",
        shortTitle: "Career valuation",
        colourAccent: "var(--adapt-calm-violet-500)",
        feature: FeatureName.CulturalNetworkCareerValuation,
        isCulturalActivityType: true,
    },
    [CulturalActivityType.CulturalIndex]: {
        type: CulturalActivityType.CulturalIndex,
        title: "Cultural indexes",
        shortTitle: "Cultural index",
        colourAccent: "var(--adapt-calm-violet-600)",
        feature: FeatureName.CulturalNetworkCulturalIndex,
        isCulturalActivityType: true,
    },
    [CulturalActivityType.PeerCatchUp]: {
        type: CulturalActivityType.PeerCatchUp,
        title: "Peer catch-ups",
        shortTitle: "Peer catch-up",
        colourAccent: "var(--adapt-calm-violet-400)",
        feature: FeatureName.PeoplePeerCatchUp,
        accessVerifier: PeerCatchupAuthService.OpenPeerCatchupDashboard,
        route$: (activity) => activity?.teamId
            ? teamCatchupsPageRoute.getRouteObject({ teamId: activity.teamId })
            : myCatchupsPageRoute.getRouteObject(),
        isCulturalActivityType: true,
    },
    [CulturalActivityType.TeamAssessment]: {
        type: CulturalActivityType.TeamAssessment,
        title: "Team assessments",
        shortTitle: "Team assessment",
        actionText: "Complete survey",
        colourAccent: "var(--adapt-open-blue-500)",
        feature: FeatureName.CulturalNetworkTeamAssessment,
        accessVerifier: TeamAssessmentAuthService.ReadAnyTeamAssessment,
        isCulturalActivityType: true,
        route$: (activity) => manageTeamAssessmentsPageRoute.getRouteObject({ teamId: activity?.teamId }),
        routeRequiresTeamId: true,
    },
    [CulturalActivityType.ResilientBusinessAssessment]: {
        type: CulturalActivityType.ResilientBusinessAssessment,
        title: "Resilient business assessments",
        shortTitle: "Resilient business assessment",
        actionText: "Complete survey",
        colourAccent: "var(--adapt-clementine-500)",
        feature: FeatureName.CulturalNetworkResilientBusinessAssessment,
        accessVerifier: ResilientBusinessAssessmentAuthService.ReadResilientBusinessAssessment,
        isCulturalActivityType: true,
        route$: () => manageRBAssessmentsPageRoute.getRouteObject(),
    },
    [CulturalActivityType.EmployeeEngagementAssessment]: {
        type: CulturalActivityType.EmployeeEngagementAssessment,
        title: "Employee engagement assessments",
        shortTitle: "Employee engagement assessment",
        actionText: "Complete survey",
        colourAccent: "var(--adapt-calm-violet-300)",
        feature: FeatureName.CulturalNetworkEmployeeEngagement,
        accessVerifier: EmployeeEngagementAuthService.ReadEmployeeEngagement,
        isCulturalActivityType: true,
        route$: () => manageEmployeeEngagementsPageRoute.getRouteObject(),
    },
};

export const CulturalActivityTypeMetadata: ICulturalActivityTypeMetadata[] = [
    CulturalActivityMetadataByType[NonCulturalActivityType.Actions],
    CulturalActivityMetadataByType[NonCulturalActivityType.Meetings],
    CulturalActivityMetadataByType[NonCulturalActivityType.Objectives],
    CulturalActivityMetadataByType[CulturalActivityType.CareerValuation],
    CulturalActivityMetadataByType[CulturalActivityType.CulturalIndex],
    CulturalActivityMetadataByType[CulturalActivityType.PeerCatchUp],
    CulturalActivityMetadataByType[CulturalActivityType.TeamAssessment],
    CulturalActivityMetadataByType[CulturalActivityType.EmployeeEngagementAssessment],
    CulturalActivityMetadataByType[CulturalActivityType.ResilientBusinessAssessment],
];

export const CulturalActivityTypes = CulturalActivityTypeMetadata.map(({ type }) => type);
